import { useState } from 'react';
import { toast } from 'sonner';
import { Pencil, Save, X, Package } from 'lucide-react';
import api from '../lib/api';

const PLAN_BADGE_COLORS = {
  starter: 'bg-blue-100 text-blue-700',
  pro: 'bg-indigo-100 text-indigo-700',
  enterprise: 'bg-slate-800 text-white',
};

const formatEUR = (cents) => {
  if (cents == null) return '--';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(cents / 100);
};

const PackagesManager = ({ packages, onRefresh }) => {
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  const startEdit = (pkg) => {
    setEditingId(pkg.id || `${pkg.app_code}-${pkg.package_code}`);
    setEditData({
      monthly_price: pkg.monthly_price ?? pkg.price_monthly ?? 0,
      annual_price: pkg.annual_price ?? pkg.price_annual ?? 0,
      modules: Array.isArray(pkg.modules) ? pkg.modules.join(', ') : (pkg.modules || ''),
      max_users: pkg.max_users ?? '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleSave = async (pkg) => {
    setSaving(true);
    try {
      const appCode = pkg.app_code || pkg.app;
      const packageCode = pkg.package_code || pkg.code || pkg.plan;
      const payload = {
        monthly_price: Number(editData.monthly_price),
        annual_price: Number(editData.annual_price),
        modules: typeof editData.modules === 'string'
          ? editData.modules.split(',').map((m) => m.trim()).filter(Boolean)
          : editData.modules,
        max_users: editData.max_users ? Number(editData.max_users) : null,
      };
      await api.put(`/api/apps/${appCode}/packages/${packageCode}`, payload);
      toast.success('Package mis a jour avec succes');
      cancelEdit();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la mise a jour');
    } finally {
      setSaving(false);
    }
  };

  if (!packages || packages.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
        <Package className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-500">Aucun package configure</p>
      </div>
    );
  }

  const pkgId = (pkg) => pkg.id || `${pkg.app_code}-${pkg.package_code}`;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left font-semibold text-slate-700 px-4 py-3">App</th>
              <th className="text-left font-semibold text-slate-700 px-4 py-3">Plan</th>
              <th className="text-right font-semibold text-slate-700 px-4 py-3">Prix mensuel</th>
              <th className="text-right font-semibold text-slate-700 px-4 py-3 hidden md:table-cell">Prix annuel</th>
              <th className="text-left font-semibold text-slate-700 px-4 py-3 hidden lg:table-cell">Modules</th>
              <th className="text-center font-semibold text-slate-700 px-4 py-3 hidden md:table-cell">Max Users</th>
              <th className="text-center font-semibold text-slate-700 px-4 py-3">Abonnes</th>
              <th className="text-right font-semibold text-slate-700 px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {packages.map((pkg) => {
              const id = pkgId(pkg);
              const isEditing = editingId === id;
              const planKey = (pkg.package_code || pkg.plan || pkg.code || '').toLowerCase();
              const badgeColor = PLAN_BADGE_COLORS[planKey] || 'bg-slate-100 text-slate-700';
              const subscriberCount = pkg.subscribers ?? pkg.subscriber_count ?? pkg.abonnes ?? 0;

              return (
                <tr key={id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <span className="font-medium text-slate-900 capitalize">
                      {pkg.app_code || pkg.app || '--'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${badgeColor}`}>
                      {pkg.package_code || pkg.plan || pkg.code || '--'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isEditing ? (
                      <input
                        type="number"
                        value={editData.monthly_price}
                        onChange={(e) => setEditData({ ...editData, monthly_price: e.target.value })}
                        className="w-24 px-2 py-1 text-right rounded border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500"
                        placeholder="Centimes"
                      />
                    ) : (
                      <span className="font-medium text-slate-900">
                        {formatEUR(pkg.monthly_price ?? pkg.price_monthly)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right hidden md:table-cell">
                    {isEditing ? (
                      <input
                        type="number"
                        value={editData.annual_price}
                        onChange={(e) => setEditData({ ...editData, annual_price: e.target.value })}
                        className="w-24 px-2 py-1 text-right rounded border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500"
                        placeholder="Centimes"
                      />
                    ) : (
                      <span className="text-slate-600">
                        {formatEUR(pkg.annual_price ?? pkg.price_annual)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editData.modules}
                        onChange={(e) => setEditData({ ...editData, modules: e.target.value })}
                        className="w-full px-2 py-1 rounded border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500"
                        placeholder="module1, module2..."
                      />
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {(Array.isArray(pkg.modules) ? pkg.modules : []).map((mod, i) => (
                          <span
                            key={i}
                            className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded"
                          >
                            {mod}
                          </span>
                        ))}
                        {(!pkg.modules || (Array.isArray(pkg.modules) && pkg.modules.length === 0)) && (
                          <span className="text-xs text-slate-400">--</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center hidden md:table-cell">
                    {isEditing ? (
                      <input
                        type="number"
                        value={editData.max_users}
                        onChange={(e) => setEditData({ ...editData, max_users: e.target.value })}
                        className="w-16 px-2 py-1 text-center rounded border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500"
                      />
                    ) : (
                      <span className="text-slate-600">{pkg.max_users ?? 'Illimite'}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-full bg-nexus-50 text-nexus-700 text-xs font-bold">
                      {subscriberCount}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isEditing ? (
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleSave(pkg)}
                          disabled={saving}
                          className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 transition-colors disabled:opacity-50"
                          title="Enregistrer"
                        >
                          <Save className="w-4 h-4" />
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 transition-colors"
                          title="Annuler"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => startEdit(pkg)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-nexus-600 hover:bg-nexus-50 transition-colors"
                        title="Modifier"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PackagesManager;
